// Endpoint:  POST /media/projects/{project_id}/documents/{filename}
// Returns:   { id, target } — `target` is a presigned S3 PUT URL.
//
// The actual file upload happens via PUT against `target` with no
// Authorization header — see step 2 below for the snippet.
//
// This is the *project-scoped* upload flow used by the canonical pipeline.
// The marketplace also exposes an org-level `/media/documents/{org}` for
// pre-staging documents before any project exists, but in practice every
// developer uploading into a Libertify experience uses the project-scoped
// path documented here.

import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import { Field, FormCard, PlaygroundPanel, Select, TextArea, TextInput } from './_shared';
import { DOCUMENT_TYPES, type DocumentType } from './_constants';

const SEQUENCE_STEPS = [
  { id: 'project', label: 'Create project', hint: 'POST /media/projects' },
  { id: 'document', label: 'Reserve slot', hint: 'POST /media/projects/{id}/documents/{filename} (you are here)' },
  { id: 'upload', label: 'PUT bytes to signed URL', hint: 'Upload the file directly to S3' },
  { id: 'generate', label: 'Generate media', hint: 'POST /media/projects/{id}/media/generate' },
];

export function DocumentPlayground({ mode }: PlaygroundProps) {
  const { organizationId } = useAuth();

  const [projectId, setProjectId] = useState('proj_xxx');
  const [filename, setFilename] = useState('whitepaper.pdf');
  const [name, setName] = useState('Q3 Whitepaper');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('Report');

  const spec: RequestSpec = useMemo(() => {
    const body: Record<string, unknown> = {
      name,
      document_type: documentType,
    };
    if (description) body.description = description;
    return {
      method: 'POST',
      path: `/media/projects/${projectId || '{project_id}'}/documents/${encodeURIComponent(
        filename || '{filename}'
      )}`,
      body,
    };
  }, [projectId, filename, name, description, documentType]);

  return (
    <PlaygroundPanel
      title="Reserve a document slot in a project"
      description="Returns the document id and a presigned S3 URL. PUT your file bytes to that URL to actually upload — no Authorization header on the PUT."
      spec={spec}
      mode={mode}
      eyebrow="Step 2 of 4 · Document upload"
      sequence={{ steps: SEQUENCE_STEPS, activeId: 'document' }}
      runLabel="Get signed URL"
    >
      <FormCard>
        <Field label="Project ID" hint="Required — paste an id returned by 'Create project'">
          <TextInput
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            placeholder="proj_…"
            required
          />
        </Field>
        <Field label="Filename" hint="Required — used in the URL path; keep the extension that matches your file">
          <TextInput value={filename} onChange={(e) => setFilename(e.target.value)} required />
        </Field>
        <Field label="Display name" hint="Required — what users see in the Libertify UI">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Document type" hint="Use 'Unknown' if your content doesn't match any specialised category">
          <Select value={documentType} onChange={(e) => setDocumentType(e.target.value as DocumentType)}>
            {DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </Field>
        <Field label="Description (optional)">
          <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </FormCard>

      <SignedUrlSnippet />
      {!organizationId && (
        <div
          className="lp-px-4 lp-py-3 lp-text-[12.5px]"
          style={{ background: '#f8f7ff', border: '1px solid #cbc4ff', borderRadius: 12, color: '#475569' }}
        >
          Tip: switch to the <strong>Quickstart flow</strong> in the sidebar to walk through project + upload + generate in one go,
          with all ids auto-piped between steps.
        </div>
      )}
    </PlaygroundPanel>
  );
}

function SignedUrlSnippet() {
  return (
    <div
      className="lp-px-5 lp-py-4"
      style={{
        background: '#f8f7ff',
        border: '1px solid #cbc4ff',
        borderRadius: 16,
      }}
    >
      <div
        className="lp-text-[10.5px] lp-font-bold lp-uppercase lp-mb-2"
        style={{ letterSpacing: '0.12em', color: '#6256ff' }}
      >
        Step 3 — PUT the bytes to the signed URL
      </div>
      <p className="lp-text-[12.5px] lp-mb-2.5" style={{ color: '#334155' }}>
        After this call returns <code style={inlineCode}>target</code>, send the file directly to S3:
      </p>
      <pre
        className="lp-text-[11.5px] lp-font-mono lp-px-3 lp-py-2.5 lp-rounded-lg lp-overflow-x-auto"
        style={{ background: '#0f172a', color: '#e2e8f0' }}
      >
{`# cURL
curl -X PUT "<target>" --upload-file ./whitepaper.pdf

// JavaScript (browser or Node)
await fetch('<target>', { method: 'PUT', body: file });
// ⚠️  Do NOT send Authorization — the URL is already signed.`}
      </pre>
    </div>
  );
}

const inlineCode: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#ffffff',
  border: '1px solid #cbc4ff',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: 11.5,
  color: '#3328aa',
};
